// web/src/routes/supplements/SupplementDetail.jsx
// One product: whose container goes in next, and who is running out.
//
// This is the screen the whole module exists for, and the measure of it is the
// morning tap. Somebody opens a fresh tub of Emcelle at six with gloves on and
// has to record it in two taps without thinking: the suggested owner is
// already filled in, and the button is the width of the page.
//
// WHY A SUGGESTION AND NOT A RULE
//
// The barn pours out of one communal tub but the unopened containers belong to
// individual owners, so somebody has to decide whose goes in. The app suggests
// whoever has the most days of cover measured against their OWN horses — which
// is fairer than taking turns, because Theodosakis has five horses on Emcelle
// and Thompson has one. It is a suggestion and not a rule because the shelf is
// a physical place and the person standing at it can see things the app can't.
//
// WHAT ISN'T SHOWN
//
// Partial containers, because nobody counts them and nobody is going to. That
// is a deliberate hole and the safety buffer in the reorder date is what pays
// for it. Saying "approximate" once, where the dates are, beats a screen that
// pretends to a precision the barn does not have.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { fmtDateNear } from '../../lib/dates';
import { useAuth } from '../../lib/auth';
import { Panel, Empty, Rows, PlannerSkeleton } from '../../components/dashboard/Planner';
import Stepper from '../../components/ui/Stepper';
import OrderingTab from './OrderingTab';
import HorseDoses from './HorseDoses';

export default function SupplementDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { activeRole } = useAuth();
  const canManage = activeRole === 'owner' || activeRole === 'manager';

  // Which tab, in the URL: Taylor's mockup puts ordering a tap away from the
  // shelf, and a tab that resets on every save loses her place mid-order.
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'ordering' ? 'ordering' : 'inventory';
  const goTab = (t) => {
    const next = new URLSearchParams(params);
    if (t === 'inventory') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
  };

  const [chosen, setChosen] = useState(null); // owner picked by hand, if any
  const [sheet, setSheet] = useState(null); // 'receive' | 'correct' | null
  const [error, setError] = useState(null);
  const [said, setSaid] = useState(null); // what just happened, in words

  const { data, isLoading } = useQuery({
    queryKey: ['supplement', id],
    queryFn: () => api.get(`/supplements/${id}`),
  });

  const owners = data?.owners ?? [];
  const s = data?.supplement;
  // The hand-picked owner wins, then the suggestion, then whoever is first.
  const ownerId = chosen ?? data?.suggestedOwnerId ?? owners[0]?.ownerUserId ?? null;
  const owner = owners.find((o) => o.ownerUserId === ownerId) ?? null;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['supplement', id] });
    qc.invalidateQueries({ queryKey: ['supplements'] });
    qc.invalidateQueries({ queryKey: ['supplement-history'] });
  };

  const open = useMutation({
    mutationFn: () => api.post(`/supplements/${id}/open`, { ownerUserId: ownerId }),
    onMutate: () => { setError(null); setSaid(null); },
    onSuccess: (r) => {
      setSaid(`Opened ${owner?.name}'s container. ${r.containers} left.`);
      // The suggestion moves on by itself: it is worked out from what is on
      // the shelves, and one of them just changed.
      setChosen(null);
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  const receive = useMutation({
    mutationFn: (body) => api.post(`/supplements/${id}/receive`, body),
    onMutate: () => { setError(null); setSaid(null); },
    onSuccess: (r, body) => {
      const who = owners.find((o) => o.ownerUserId === body.ownerUserId);
      setSaid(`Added ${body.quantity} for ${who?.name ?? 'them'}. ${r.containers} on the shelf.`);
      setSheet(null);
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  const correct = useMutation({
    mutationFn: (body) => api.post(`/supplements/${id}/correct`, body),
    onMutate: () => { setError(null); setSaid(null); },
    onSuccess: (r, body) => {
      const who = owners.find((o) => o.ownerUserId === body.ownerUserId);
      setSaid(`Set ${who ? `${who.name}'s` : 'their'} count to ${r.containers}.`);
      setSheet(null);
      refresh();
    },
    onError: (e) => setError(friendly(e)),
  });

  const busy = open.isPending || receive.isPending || correct.isPending;

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <PlannerSkeleton />
      </div>
    );
  }
  if (!s) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <p className="card p-6 text-sm text-ink-500">
          That supplement is gone.{' '}
          <Link to="/supplements" className="text-hunter hover:underline">Back to the list →</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <header className="flex items-start justify-between gap-3 mb-4 pb-2 border-b-2 border-hunter/30">
        <div className="min-w-0">
          <Link to="/supplements" className="text-xs text-hunter hover:underline">← Supplements</Link>
          <h1 className="text-lg md:text-xl font-semibold leading-tight truncate">
            {s.name}
            {s.kind === 'medication' && (
              <span className="ml-1.5 align-middle text-[10px] font-semibold uppercase tracking-wider text-sky-700">Rx</span>
            )}
          </h1>
          <p className="text-sm text-ink-500 truncate">
            {[s.vendorName, s.containerLabel, s.dosesPerContainer && `${trim(s.dosesPerContainer)} ${s.doseUnit}s a container`]
              .filter(Boolean)
              .join(' · ') || 'No vendor or container size yet'}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 pt-4">
          <Link to="/supplements/history" className="text-sm text-hunter hover:underline">History</Link>
          {canManage && (
            <Link to={`/supplements/${id}/edit`} className="text-sm text-hunter hover:underline">Edit</Link>
          )}
        </div>
      </header>

      {/* Setup gaps first. Every number below is derived from these, so a
          missing one is not a footnote — it is why the page is quiet. */}
      {(s.needsYield || data.unpaidHorses?.length > 0) && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 space-y-1">
          {s.needsYield && (
            <p>
              Nobody has recorded how many {s.doseUnit}s are in a container, so nothing can be
              predicted for this yet.{' '}
              {canManage && <Link to={`/supplements/${id}/edit`} className="underline">Add it →</Link>}
            </p>
          )}
          {data.unpaidHorses?.length > 0 && (
            <p>
              {data.unpaidHorses.map((h) => h.name).join(', ')}{' '}
              {data.unpaidHorses.length === 1 ? 'has' : 'have'} no owner to draw from — co-owned
              with nobody marked as the billing contact. {data.unpaidHorses.length === 1 ? 'It is' : 'They are'}{' '}
              being fed and counted against nobody&apos;s shelf.
            </p>
          )}
        </div>
      )}

      {/* Inventory and Ordering, as in the mockup. The same owners seen two
          ways: what is on their shelf, and what to do about it. */}
      <div className="flex gap-1 mb-4 p-1 rounded-lg bg-hunter/10" role="tablist">
        {[['inventory', 'Inventory'], ['ordering', 'Ordering']].map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            onClick={() => goTab(k)}
            className={
              'flex-1 py-1.5 rounded-md text-sm font-medium ' +
              (tab === k ? 'bg-white text-hunter shadow-sm' : 'text-hunter/70 hover:text-hunter')
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'ordering' ? (
        <OrderingTab
          supplementId={id}
          owners={owners.map((o) => ({ ...o, vendorName: s.vendorName }))}
          today={data.today}
          canManage={canManage}
        />
      ) : (
      <div className="space-y-4">
        <Panel
          title="Open a container"
          action={
            data.lastOpened ? (
              <span className="normal-case tracking-normal font-normal text-hunter/80">
                last from {data.lastOpened.ownerName}
              </span>
            ) : null
          }
        >
          {owners.length === 0 ? (
            <Empty>
              Nobody has stock or horses on this yet.{' '}
              {canManage && <Link to={`/supplements/${id}/edit`} className="text-hunter hover:underline">Set it up →</Link>}
            </Empty>
          ) : (
            <div className="p-3 space-y-3">
              {data.suggestedOwnerId && (
                <p className="text-sm text-ink-600">
                  Suggested:{' '}
                  <strong className="text-hunter">
                    {owners.find((o) => o.ownerUserId === data.suggestedOwnerId)?.name}
                  </strong>
                  <span className="block text-xs text-ink-400">
                    {/* Say the rule out loud. A suggestion whose reasoning is
                        invisible gets overridden out of suspicion. */}
                    furthest from running out, counting their own horses
                  </span>
                </p>
              )}

              <label className="block">
                <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">
                  Whose container are you opening?
                </span>
                <select
                  className="input"
                  value={ownerId ?? ''}
                  onChange={(e) => setChosen(e.target.value)}
                  disabled={busy}
                >
                  {owners.map((o) => (
                    <option key={o.ownerUserId} value={o.ownerUserId} disabled={o.containers <= 0}>
                      {o.name} · {o.containers} {o.containers === 1 ? 'left' : 'left'}
                      {o.containers <= 0 ? ' — none' : ''}
                    </option>
                  ))}
                </select>
              </label>

              {/* 56px tall. This gets pressed in an aisle, in winter, with
                  gloves on, by somebody holding a tub in the other hand. */}
              <button
                type="button"
                onClick={() => open.mutate()}
                disabled={busy || !owner || owner.containers <= 0}
                className="w-full min-h-14 rounded-lg bg-hunter text-white text-base font-medium
                           hover:bg-hunter-light active:bg-hunter-dark
                           disabled:bg-ink-200 disabled:text-ink-400"
              >
                {open.isPending
                  ? 'Recording…'
                  : owner && owner.containers <= 0
                    ? `${owner.name} has none left`
                    : `Opened one of ${owner?.name ?? ''}'s`}
              </button>

              {canManage && (
                <div className="flex gap-2">
                  <button type="button" className="btn-secondary flex-1" disabled={busy}
                    onClick={() => { setSheet('receive'); setError(null); setSaid(null); }}>
                    Delivery arrived
                  </button>
                  <button type="button" className="btn-secondary flex-1" disabled={busy}
                    onClick={() => { setSheet('correct'); setError(null); setSaid(null); }}>
                    Correct a count
                  </button>
                </div>
              )}

              {said && <p className="text-sm text-hunter">{said}</p>}
              {error && <p className="text-sm text-rose-700">{error}</p>}
            </div>
          )}
        </Panel>

        <HorseDoses
          supplementId={id}
          owners={owners}
          doseUnit={s.doseUnit}
          defaultDailyDoses={s.defaultDailyDoses}
          canManage={canManage}
        />

        <Panel title="Owner reserves" count={owners.length || null}>
          {owners.length === 0 ? (
            <Empty>Nothing on the shelf.</Empty>
          ) : (
            <Rows>
              {owners.map((o) => (
                <li key={o.ownerUserId} className="px-3 py-2 text-sm">
                  <OwnerLine owner={o} today={data.today} doseUnit={s.doseUnit} />
                </li>
              ))}
            </Rows>
          )}
          {/* Said once, where the dates are, rather than on every row. */}
          {owners.some((o) => o.emptyOn) && (
            <p className="px-3 pb-2 text-[11px] text-ink-400">
              Dates are approximate — what is left in the open tub is not counted.
            </p>
          )}
        </Panel>
      </div>
      )}

      {sheet && (
        <StockSheet
          mode={sheet}
          owners={owners}
          defaultOwnerId={ownerId}
          busy={busy}
          error={error}
          onClose={() => setSheet(null)}
          onSubmit={(body) => (sheet === 'receive' ? receive.mutate(body) : correct.mutate(body))}
        />
      )}
    </div>
  );
}

/** One owner's shelf: what they have, how long it lasts, what to do about it. */
function OwnerLine({ owner: o, today, doseUnit }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="min-w-0 flex-1">
        <span className="block font-medium truncate">{o.name}</span>
        <span className="block text-xs text-ink-400 truncate">
          {o.horses.length === 0
            ? 'no horses on it'
            : `${o.horses.map((h) => h.name).join(', ')} · ${trim(o.dailyDoses)} ${doseUnit}${o.dailyDoses === 1 ? '' : 's'} a day`}
        </span>
        <StatusLine owner={o} today={today} />
      </span>
      <span className="shrink-0 text-right">
        <span className="block font-semibold tabular-nums leading-none">{o.containers}</span>
        <span className="block text-[11px] text-ink-400">
          {o.daysOfCover != null ? `${o.daysOfCover}d` : 'unopened'}
        </span>
      </span>
    </div>
  );
}

function StatusLine({ owner: o, today }) {
  if (o.status === 'autoship') {
    return (
      <span className="block text-xs text-sky-700">
        On autoship{o.autoship?.nextExpected ? ` · due ${fmtDateNear(o.autoship.nextExpected, today)}` : ''} — nothing to order
      </span>
    );
  }
  if (o.status === 'out') {
    return <span className="block text-xs text-rose-700 font-medium">Out — nothing on the shelf</span>;
  }
  if (o.status === 'due') {
    return (
      <span className="block text-xs text-amber-700 font-medium">
        Order now{o.emptyOn ? ` · empty ${fmtDateNear(o.emptyOn, today)}` : ''}
      </span>
    );
  }
  if (o.status === 'idle') {
    return <span className="block text-xs text-ink-400">Stock, but no horses on it</span>;
  }
  if (o.status === 'unknown') {
    return <span className="block text-xs text-ink-400">Can&apos;t work out a date yet</span>;
  }
  if (o.reorderOn) {
    return (
      <span className="block text-xs text-hunter">
        Order by {fmtDateNear(o.reorderOn, today)}
        {o.emptyOn && ` · empty ${fmtDateNear(o.emptyOn, today)}`}
      </span>
    );
  }
  return null;
}

/**
 * Receiving a delivery, or correcting a count.
 *
 * Deliberately one sheet with two modes, because they are the same gesture
 * from the user's side — pick an owner, type a number — and the difference is
 * one word in the button. The difference that matters is what the number MEANS,
 * so each mode says it: "how many arrived" against "what is actually there".
 */
function StockSheet({ mode, owners, defaultOwnerId, busy, error, onClose, onSubmit }) {
  const receiving = mode === 'receive';
  const [ownerUserId, setOwnerUserId] = useState(defaultOwnerId ?? owners[0]?.ownerUserId ?? '');
  const owner = owners.find((o) => o.ownerUserId === ownerUserId) ?? null;
  const have = owner?.containers ?? 0;

  // A correction opens on WHAT THE APP THINKS IS THERE, not on nothing.
  //
  // It opened on 0, which made the fastest possible gesture — open the sheet,
  // tap the green button — empty somebody's shelf. The screenshot that came
  // back showed "Pavla Nygaard · 1 now" above a box reading 0, one tap from
  // throwing her bottle away. Starting at the current count also makes the
  // minus and plus buttons mean the obvious thing: one more, or one fewer,
  // than the app believes.
  const [qty, setQty] = useState(String(receiving ? 1 : have));
  // Following the owner is the point: picking a different person has to
  // re-aim the correction at THEIR shelf, or it carries the last one's number.
  const [followed, setFollowed] = useState(ownerUserId);
  if (!receiving && ownerUserId !== followed) {
    setFollowed(ownerUserId);
    setQty(String(have));
  }

  const n = Number(qty);
  const valid = qty !== '' && Number.isInteger(n) && (receiving ? n > 0 : n >= 0) && !!ownerUserId;
  // What the button is about to do, in the number it will leave behind.
  const after = receiving ? have + (Number.isFinite(n) ? n : 0) : n;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          onSubmit(receiving ? { ownerUserId, quantity: n } : { ownerUserId, countedTotal: n });
        }}
        role="dialog"
        aria-modal="true"
        aria-label={receiving ? 'Record a delivery' : 'Correct a count'}
        className="bg-white w-full sm:max-w-sm rounded-t-xl sm:rounded-xl overflow-hidden"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug">
              {receiving ? 'Delivery arrived' : 'Correct a count'}
            </h2>
            <p className="text-sm text-ink-500">
              {receiving
                ? 'Full unopened containers only.'
                : 'What is actually on the shelf right now.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0">
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <label className="block">
            <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">Owner</span>
            <select className="input" value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)} disabled={busy}>
              {owners.map((o) => (
                <option key={o.ownerUserId} value={o.ownerUserId}>
                  {o.name} · {o.containers} now
                </option>
              ))}
            </select>
          </label>

          <div>
            <span className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">
              {receiving ? 'How many arrived' : 'Counted on the shelf'}
            </span>
            <Stepper
              value={qty}
              onChange={setQty}
              min={receiving ? 1 : 0}
              disabled={busy}
              label={receiving ? 'containers arrived' : 'containers counted'}
            />
            {/* Says where it lands before it lands there. On a correction the
                arithmetic is invisible otherwise — "counted 3" against a shelf
                the app thinks holds 5 is a change of minus two, and that is
                the number worth seeing before you commit it. */}
            <p className="mt-1.5 text-xs text-ink-500">
              {!valid
                ? '\u00a0'
                : receiving
                  ? `${owner?.name ?? 'They'} will have ${after}.`
                  : after === have
                    ? `That is what the app already has.`
                    : `${have} \u2192 ${after} for ${owner?.name ?? 'them'}.`}
            </p>
          </div>

          {!receiving && (
            // A correction is not an adjustment by N, it is "the shelf says 9".
            // The ledger keeps both, and this is where the difference is said.
            <p className="text-xs text-ink-400">
              This records the count you made, and keeps what it changed from.
            </p>
          )}

          {error && <p className="text-sm text-rose-700">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-ink-100">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy || !valid || (!receiving && after === have)}>
            {busy ? 'Saving…' : receiving ? 'Add to the shelf' : `Set it to ${valid ? after : '—'}`}
          </button>
        </div>
      </form>
    </div>
  );
}

/** 2 rather than 2.0, 0.75 rather than 0.750000000001. */
export function trim(n) {
  if (n == null) return '';
  return String(Math.round(n * 100) / 100);
}

export function friendly(err) {
  const code = err?.body?.error || err?.message;
  switch (code) {
    case 'no_connection':
      return `That didn't save — couldn't reach the server, so nothing changed. Check your signal and try again (${err.detail}).`;
    case 'timed_out':
      return "That didn't save — the server took too long to answer, so nothing changed.";
    case 'none_on_the_shelf':
      return 'There are none of theirs left. Pick somebody else.';
    case 'already_that_count':
      return 'That is already the count.';
    case 'already_undone':
      return 'Somebody already undid that one.';
    case 'cannot_undo_an_undo':
      return 'That entry is itself an undo.';
    case 'not_allowed':
      return 'Only an owner or manager can do that.';
    case 'invalid_quantity':
      return 'That has to be a whole number.';
    case 'not_found':
      return 'That is already gone.';
    default:
      return `That didn't save — ${code || 'no reason given'}${err?.status ? ` (${err.status})` : ''}.`;
  }
}
